/**
 * Query-builder model for the advanced search overlay (pure, no React).
 *
 * A ConditionGroup mirrors pandora's nested find query (docs/pandora-api-reference.md §2):
 * rows become conditions, sub-groups become nested {conditions, operator} objects,
 * negation prefixes the comparison operator with '!'. `buildQuery` prunes empty
 * values/groups so a blank builder yields the match-all query {conditions: []}.
 */
import type { PandoraCondition, PandoraQuery } from './pandora';

export interface ConditionRow {
  key: string;
  operator: string; // '=' contains, '==' is, '^' starts, '$' ends, '<', '<=', '>', '>='
  value: string | number | boolean;
  negate: boolean;
}

export interface ConditionGroup {
  operator: '&' | '|';
  rows: ConditionRow[];
  groups: ConditionGroup[];
}

export function emptyGroup(operator: '&' | '|' = '&'): ConditionGroup {
  return { operator, rows: [], groups: [] };
}

export function emptyRow(key = '*'): ConditionRow {
  return { key, operator: '=', value: '', negate: false };
}

function rowCondition(row: ConditionRow): PandoraCondition | null {
  const value = typeof row.value === 'string' ? row.value.trim() : row.value;
  if (value === '') return null;
  return { key: row.key, operator: row.negate ? `!${row.operator}` : row.operator, value };
}

function groupQuery(group: ConditionGroup): PandoraQuery {
  const conditions: (PandoraCondition | PandoraQuery)[] = [];
  for (const row of group.rows) {
    const c = rowCondition(row);
    if (c) conditions.push(c);
  }
  for (const sub of group.groups) {
    const q = groupQuery(sub);
    if (q.conditions.length > 0) conditions.push(q);
  }
  return { conditions, operator: group.operator };
}

/**
 * Build the pandora find query for a builder group. `extra` conditions
 * (e.g. {canplayvideo == true}) are always ANDed with the built query.
 */
export function buildQuery(group: ConditionGroup, extra?: PandoraCondition[]): PandoraQuery {
  const query = groupQuery(group);
  if (!extra || extra.length === 0) return query;
  if (query.conditions.length === 0) return { conditions: [...extra], operator: '&' };
  if (query.operator === '&') return { ...query, conditions: [...query.conditions, ...extra] };
  return { conditions: [query, ...extra], operator: '&' };
}
